import { isValidCnpj, normalizeZipCode } from "../../utils/documentFormat"
import { type ISaveFiscalIssuerSettings } from "./IFiscalIssuerSettings"

export const BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const

export type FiscalIssuerField = keyof ISaveFiscalIssuerSettings

export type FiscalIssuerValidationMessageKey =
  | "issuer.validation.cnpj"
  | "issuer.validation.legalName"
  | "issuer.validation.tradeName"
  | "issuer.validation.stateRegistration"
  | "issuer.validation.street"
  | "issuer.validation.number"
  | "issuer.validation.complement"
  | "issuer.validation.neighborhood"
  | "issuer.validation.city"
  | "issuer.validation.state"
  | "issuer.validation.zipCode"

export type FiscalIssuerValidationErrors = Partial<Record<FiscalIssuerField, FiscalIssuerValidationMessageKey>>

const hasLengthBetween = (value: string, minimum: number, maximum: number) => {
  const length = value.trim().length

  return length >= minimum && length <= maximum
}

export const validateFiscalIssuerSettings = (issuer: ISaveFiscalIssuerSettings): FiscalIssuerValidationErrors => {
  const errors: FiscalIssuerValidationErrors = {}

  const stateRegistration = issuer.stateRegistration.replace(/\D/g, "")

  const state = issuer.state.trim().toUpperCase()

  if (!isValidCnpj(issuer.cnpj)) {
    errors.cnpj = "issuer.validation.cnpj"
  }

  if (!hasLengthBetween(issuer.legalName, 2, 60)) {
    errors.legalName = "issuer.validation.legalName"
  }

  if (issuer.tradeName.trim().length > 60) {
    errors.tradeName = "issuer.validation.tradeName"
  }

  if (stateRegistration.length < 2 || stateRegistration.length > 14) {
    errors.stateRegistration = "issuer.validation.stateRegistration"
  }

  if (!hasLengthBetween(issuer.street, 2, 60)) {
    errors.street = "issuer.validation.street"
  }

  if (!hasLengthBetween(issuer.number, 1, 60)) {
    errors.number = "issuer.validation.number"
  }

  if (issuer.complement.trim().length > 60) {
    errors.complement = "issuer.validation.complement"
  }

  if (!hasLengthBetween(issuer.neighborhood, 2, 60)) {
    errors.neighborhood = "issuer.validation.neighborhood"
  }

  if (!hasLengthBetween(issuer.city, 2, 60)) {
    errors.city = "issuer.validation.city"
  }

  if (!BRAZILIAN_STATES.some((candidate) => candidate === state)) {
    errors.state = "issuer.validation.state"
  }

  if (normalizeZipCode(issuer.zipCode).length !== 8) {
    errors.zipCode = "issuer.validation.zipCode"
  }

  return errors
}
